import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { scholarPortalAPI } from '../../services/api';
import '../../styles/components.css';

const Publications = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [scholarProfile, setScholarProfile] = useState(null);

    const [publications, setPublications] = useState([]);
    const [stats, setStats] = useState(null);
    const [compliance, setCompliance] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedPubIds, setSelectedPubIds] = useState(new Set());
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);

    const getPubDate = (pub) => {
        if (!pub) return null;
        if (pub.publicationDate) {
            const d = new Date(pub.publicationDate);
            if (!isNaN(d.getTime())) return d;
        }
        if (pub.date) {
            const d = new Date(pub.date);
            if (!isNaN(d.getTime())) return d;
        }
        if (pub.createdAt) {
            if (typeof pub.createdAt === 'object') {
                const sec = pub.createdAt._seconds || pub.createdAt.seconds;
                if (sec) return new Date(sec * 1000);
            } else {
                const d = new Date(pub.createdAt);
                if (!isNaN(d.getTime())) return d;
            }
        }
        const y = Number(pub.year);
        if (y && !isNaN(y)) {
            return new Date(y, 0, 1);
        }
        return null;
    };

    const formatPubDate = (pub) => {
        if (!pub) return 'N/A';
        if (pub.publicationDate) {
            const d = new Date(pub.publicationDate);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            }
            return String(pub.publicationDate);
        }
        if (pub.date) {
            const d = new Date(pub.date);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            }
        }
        if (pub.createdAt) {
            let d = null;
            if (typeof pub.createdAt === 'object') {
                const sec = pub.createdAt._seconds || pub.createdAt.seconds;
                if (sec) d = new Date(sec * 1000);
            } else {
                d = new Date(pub.createdAt);
            }
            if (d && !isNaN(d.getTime())) {
                return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            }
        }
        return pub.year ? String(pub.year) : 'N/A';
    };

    useEffect(() => {
        if (!publications || publications.length === 0) {
            setSelectedPubIds(new Set());
            return;
        }

        if (!startDate && !endDate) {
            setSelectedPubIds(new Set(publications.map(p => p.id)));
            return;
        }

        const matchingIds = publications.filter(pub => {
            const pubDate = getPubDate(pub);
            if (!pubDate) return true;

            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (pubDate < start) return false;
            }

            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (pubDate > end) return false;
            }

            return true;
        }).map(p => p.id);

        setSelectedPubIds(new Set(matchingIds));
    }, [startDate, endDate, publications]);

    const togglePubSelection = (id) => {
        setSelectedPubIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedPubIds(new Set(publications.map(p => p.id)));
    };

    const handleDeselectAll = () => {
        setSelectedPubIds(new Set());
    };

    const handleDownloadPDF = async () => {
        const categoryOrder = { 'journal': 1, 'conference': 2, 'book-chapter': 3, 'book chapter': 3, 'chapter': 3 };
        const statusOrder = { 'published': 1, 'accepted': 2, 'under review': 3, 'underreview': 3, 'under_review': 3, 'submitted': 4 };

        const selectedPubs = [...publications].sort((a, b) => {
            const catA = categoryOrder[String(a.category || '').toLowerCase().trim()] || 99;
            const catB = categoryOrder[String(b.category || '').toLowerCase().trim()] || 99;
            if (catA !== catB) return catA - catB;

            const statA = statusOrder[String(a.status || '').toLowerCase().trim()] || 99;
            const statB = statusOrder[String(b.status || '').toLowerCase().trim()] || 99;
            return statA - statB;
        });

        if (!selectedPubs || selectedPubs.length === 0) {
            alert('No publications available to download in the combined PDF.');
            return;
        }

        setIsPdfGenerating(true);
        try {
            const { jsPDF } = await import('jspdf');
            const { default: autoTable } = await import('jspdf-autotable');

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth(); // 297mm
            const pageH = doc.internal.pageSize.getHeight(); // 210mm

            // Try loading logo
            const logoImg = new Image();
            logoImg.src = '/tcelogo.png';
            await new Promise(r => { logoImg.onload = r; logoImg.onerror = r; });

            // ── PAGE 1: COVER / SUMMARY PAGE (LANDSCAPE) ──
            let y = 14;

            try {
                doc.addImage(logoImg, 'PNG', 14, 10, 20, 20);
            } catch (e) {
                // Logo optional
            }

            // Header text
            doc.setFontSize(19);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 58, 138); // #1e3a8a
            doc.text('THIAGARAJAR COLLEGE OF ENGINEERING', pageW / 2 + 5, y, { align: 'center' });
            y += 7.5;

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(75, 85, 99);
            doc.text('Thirupparankundram, Madurai - 625 015, Tamil Nadu', pageW / 2 + 5, y, { align: 'center' });
            y += 6.5;

            doc.setFontSize(14.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(185, 28, 28); // #b91c1c
            doc.text('THIAGARAJAR RESEARCH FELLOWSHIP (TRF)', pageW / 2 + 5, y, { align: 'center' });
            y += 6.5;

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 58, 138);
            doc.text('SCHOLAR PUBLICATIONS REPORT', pageW / 2 + 5, y, { align: 'center' });
            y += 8.5;

            doc.setDrawColor(30, 58, 138);
            doc.setLineWidth(0.8);
            doc.line(14, y, pageW - 14, y);
            y += 8;

            // Scholar Details Box (Left Side) & Selection Summary Box (Right Side)
            const boxW = (pageW - 35) / 2; // ~131mm each
            const boxH = 68;

            // Scholar Details Box (Left)
            doc.setFillColor(240, 246, 255);
            doc.setDrawColor(191, 219, 254);
            doc.roundedRect(14, y, boxW, boxH, 3, 3, 'FD');

            doc.setFontSize(12.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 58, 138);
            doc.text('SCHOLAR DETAILS', 20, y + 9);

            doc.setFontSize(10.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(31, 41, 55);

            const scholarName = scholarProfile?.name || scholarProfile?.scholarName || user?.fullName || 'N/A';
            const rollNo = scholarProfile?.rollno || scholarProfile?.rollNo || scholarProfile?.tceRollNo || 'N/A';
            const dept = scholarProfile?.dept || scholarProfile?.department || 'N/A';
            const email = scholarProfile?.email || user?.email || 'N/A';
            const supervisor = scholarProfile?.Supervisor || scholarProfile?.supervisor || scholarProfile?.supervisorName || scholarProfile?.SupervisorName || scholarProfile?.guide || user?.supervisor || user?.Supervisor || 'N/A';
            const fellowship = scholarProfile?.fellowshipStatus || 'Active';
            const admittedDate = scholarProfile?.trfAdmittedDate || scholarProfile?.month || scholarProfile?.admittedMonthYear || 'N/A';

            doc.text(`Scholar Name: ${scholarName}`, 20, y + 16);
            doc.text(`Email: ${email}`, 20, y + 22.5);
            doc.text(`Roll / Register No: ${rollNo}`, 20, y + 29);
            doc.text(`Supervisor: ${supervisor}`, 20, y + 35.5);
            doc.text(`Department: ${dept}`, 20, y + 42);
            doc.text(`TRF Admitted Month/Year: ${admittedDate}`, 20, y + 48.5);
            doc.text(`Fellowship Status: ${fellowship}`, 20, y + 55);
            doc.text(`Generated On: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 20, y + 61.5);

            // Report Summary Box (Right)
            const rightX = 14 + boxW + 7;
            doc.setFillColor(244, 251, 247);
            doc.setDrawColor(167, 243, 208);
            doc.roundedRect(rightX, y, boxW, boxH, 3, 3, 'FD');

            doc.setFontSize(12.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(15, 118, 110);
            doc.text('PUBLICATIONS SUMMARY', rightX + 6, y + 9);

            const journalCount = selectedPubs.filter(p => p.category === 'journal').length;
            const confCount = selectedPubs.filter(p => p.category === 'conference').length;
            const bookCount = selectedPubs.filter(p => p.category === 'book-chapter').length;
            const scopusCount = selectedPubs.filter(p => p.scopusIndexed).length;
            const dateRangeStr = (startDate || endDate)
                ? `${startDate || 'Earliest'} to ${endDate || 'Latest'}`
                : 'All Available Dates';

            doc.setFontSize(10.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(31, 41, 55);

            doc.text('Date Range Selected: All Available Dates', rightX + 6, y + 16);
            doc.text(`Total Publications: ${selectedPubs.length}`, rightX + 6, y + 23.5);
            doc.text(`Journals: ${journalCount}`, rightX + 6, y + 31);
            doc.text(`Conferences: ${confCount}`, rightX + 6, y + 38.5);
            doc.text(`Chapters: ${bookCount}`, rightX + 6, y + 46);
            doc.text(`Scopus Indexed Publications: ${scopusCount}`, rightX + 6, y + 53.5);

            y += boxH + 10;

            // Note section
            doc.setFontSize(12.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 58, 138); // #1e3a8a
            doc.text('Note: Proofs are attached for each publication.', 14, y);
            y += 7;

            doc.setFontSize(11);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(107, 114, 128);
            doc.text('This cover page summarizes scholar credentials and filter criteria. The complete structured table follows.', 14, y);

            // ── PAGE 2+: DETAILED PUBLICATIONS TABLE IN SEPARATE COLUMNS ──
            doc.addPage();
            y = 14;

            const head = [[
                '#',
                'Title',
                'Authors & Correspondence',
                'Category',
                'Journal / Conference Name',
                'Date / Year',
                'Vol / Issue / Pages',
                'DOI / GDrive Link (Proof)',
                'Scopus / Quartile',
                'Ack & Status'
            ]];

            const body = selectedPubs.map((pub, idx) => {
                const authorsStr = Array.isArray(pub.authors) ? pub.authors.join(', ') : pub.authors || 'N/A';
                const corrStr = pub.correspondingAuthor ? `\n(Corr: ${pub.correspondingAuthor})` : '';

                const vip = [];
                if (pub.volume) vip.push(`Vol: ${pub.volume}`);
                if (pub.issue) vip.push(`Issue: ${pub.issue}`);
                if (pub.pages) vip.push(`Pages: ${pub.pages}`);
                const vipStr = vip.length > 0 ? vip.join('\n') : 'N/A';

                return [
                    idx + 1,
                    pub.title || 'N/A',
                    `${authorsStr}${corrStr}`,
                    (pub.category || 'journal').toUpperCase(),
                    pub.venue || 'N/A',
                    formatPubDate(pub),
                    vipStr,
                    pub.doi || 'N/A',
                    `Scopus: ${pub.scopusIndexed ? 'Yes' : 'No'}\nQuartile: ${pub.quartile || 'N/A'}`,
                    `Status: ${(pub.status || 'submitted').toUpperCase()}\nTRF Ack: ${pub.trfAcknowledgement ? 'Yes' : 'No'}`
                ];
            });

            autoTable(doc, {
                theme: 'grid',
                startY: y,
                head,
                body,
                styles: { fontSize: 9.5, cellPadding: 3, valign: 'top', textColor: [30, 41, 59] },
                headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center' },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 8 },
                    1: { cellWidth: 44 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 24, halign: 'center' },
                    4: { cellWidth: 40, fontSize: 9 },
                    5: { cellWidth: 22, halign: 'center' },
                    6: { cellWidth: 24 },
                    7: { cellWidth: 29, fontSize: 8.5 },
                    8: { cellWidth: 22 },
                    9: { cellWidth: 21 }
                },
                margin: { top: 14, bottom: 35, left: 14, right: 14 }
            });

            // ── SIGNATURES & FOOTER ON EVERY SINGLE PAGE ──
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);

                // Draw 4 signatures at bottom of every page (Scholar, Supervisor, DLC, HOD)
                const sigY = pageH - 24;
                doc.setDrawColor(156, 163, 175);
                doc.setLineWidth(0.4);
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(55, 65, 81);

                // 1. Scholar Signature
                doc.line(20, sigY, 65, sigY);
                doc.text('Scholar Signature', 42.5, sigY + 4.5, { align: 'center' });

                // 2. Supervisor Signature
                doc.line(90, sigY, 135, sigY);
                doc.text('Supervisor Signature', 112.5, sigY + 4.5, { align: 'center' });

                // 3. DLC Signature
                doc.line(160, sigY, 205, sigY);
                doc.text('DLC Signature', 182.5, sigY + 4.5, { align: 'center' });

                // 4. HOD Signature
                doc.line(230, sigY, 275, sigY);
                doc.text('HOD Signature', 252.5, sigY + 4.5, { align: 'center' });

                // Footer page numbering
                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(107, 114, 128);
                doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 8, { align: 'center' });
                doc.text('TRF System - Thiagarajar College of Engineering', 14, pageH - 8);
                doc.text(new Date().toLocaleDateString('en-IN'), pageW - 14, pageH - 8, { align: 'right' });
            }

            const cleanScholarName = scholarName.replace(/[^a-zA-Z0-9]/g, '_');
            doc.save(`Publications_${cleanScholarName}_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsPdfGenerating(false);
        }
    };

    const initialFormState = {
        category: 'journal',
        title: '',
        authors: '',
        correspondingAuthor: '',
        venue: '',
        doi: '',
        publicationDate: '',
        year: new Date().getFullYear(),
        volume: '',
        issue: '',
        pages: '',
        scopusIndexed: false,
        quartile: '',
        trfAcknowledgement: false,
        acknowledgementText: '',
        status: 'submitted'
    };

    const [formData, setFormData] = useState(initialFormState);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const getNavigationLinks = () => {
        return [
            { path: '/scholar/dashboard', label: 'Dashboard' },
            { path: '/scholar/publications', label: 'My Publications' },
            { path: '/scholar/update-bank-details', label: 'Update Bank Details' },
            { path: '/scholar/monthly-claim', label: 'Monthly Claim Form' },
            // { path: 'https://drive.google.com/file/d/1vht_RfO9R7Ygqam1XZN4AUWv8BXIzPll/view?usp=drive_link', label: 'TRF Guidelines', isExternal: true },
        ];
    };

    const navLinks = getNavigationLinks();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchProfile = async () => {
            try {
                const response = await scholarPortalAPI.getProfile();
                if (response.data && response.data.success) {
                    setScholarProfile(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching scholar profile:', error);
            }
        };
        fetchProfile();
        fetchPublications();
        fetchStats();
        fetchCompliance();
    }, [user, navigate]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setSidebarOpen(true);
            } else {
                setSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile && sidebarOpen) {
                const sidebar = document.querySelector('.sidebar');
                const hamburger = document.querySelector('.hamburger-menu');
                if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
                    setSidebarOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobile, sidebarOpen]);

    const fetchPublications = async () => {
        try {
            const response = await scholarPortalAPI.getMyPublications();
            setPublications(response.data);
        } catch (error) {
            console.error('Error fetching publications:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await scholarPortalAPI.getPublicationStats();
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchCompliance = async () => {
        try {
            const response = await scholarPortalAPI.getComplianceCheck();
            setCompliance(response.data);
        } catch (error) {
            console.error('Error fetching compliance:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const next = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            if (name === 'publicationDate' && value) {
                const pYear = new Date(value).getFullYear();
                if (pYear && !isNaN(pYear)) {
                    next.year = pYear;
                }
            }
            return next;
        });
    };

    const handleEdit = (pub) => {
        setFormData({
            category: pub.category || 'journal',
            title: pub.title || '',
            authors: Array.isArray(pub.authors) ? pub.authors.join(', ') : pub.authors || '',
            correspondingAuthor: pub.correspondingAuthor || '',
            venue: pub.venue || '',
            doi: pub.doi || '',
            publicationDate: pub.publicationDate || (pub.date ? String(pub.date).slice(0, 10) : ''),
            year: pub.year || new Date().getFullYear(),
            volume: pub.volume || '',
            issue: pub.issue || '',
            pages: pub.pages || '',
            scopusIndexed: pub.scopusIndexed || false,
            quartile: pub.quartile || '',
            trfAcknowledgement: pub.trfAcknowledgement || false,
            acknowledgementText: pub.acknowledgementText || '',
            status: pub.status || 'submitted'
        });
        setEditingId(pub.id);
        setShowAddForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleAddForm = () => {
        if (showAddForm) {
            setShowAddForm(false);
            setEditingId(null);
            setFormData(initialFormState);
        } else {
            setShowAddForm(true);
            setEditingId(null);
            setFormData(initialFormState);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const authorsArray = formData.authors.split(',').map(a => a.trim());
            const payload = {
                ...formData,
                authors: authorsArray
            };

            if (editingId) {
                await scholarPortalAPI.updatePublication(editingId, payload);
            } else {
                await scholarPortalAPI.addPublication(payload);
            }

            alert(`Publication ${editingId ? 'updated' : 'added'} successfully!`);
            setShowAddForm(false);
            setEditingId(null);
            setFormData(initialFormState);
            fetchPublications();
            fetchStats();
            fetchCompliance();

        } catch (error) {
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this publication?')) return;

        try {
            await scholarPortalAPI.deletePublication(id);
            alert('Publication deleted successfully!');
            fetchPublications();
            fetchStats();
            fetchCompliance();
        } catch (error) {
            alert('Failed to delete publication');
        }
    };

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside
                className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}
                style={isMobile ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    zIndex: 1000,
                    width: '250px',
                    transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
                } : {}}
            >
                <div className="sidebar-header">
                    <h2>TRF System</h2>
                    {!isMobile && (
                        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                            {sidebarOpen ? '◀' : '▶'}
                        </button>
                    )}
                    {isMobile && (
                        <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', marginLeft: 'auto' }}>
                            ✕
                        </button>
                    )}
                </div>

                <nav className="sidebar-nav">
                    {navLinks.map((link) => (
                        link.isExternal ? (
                            <a
                                key={link.path}
                                href={link.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                            >
                                {sidebarOpen && <span className="nav-label">{link.label}</span>}
                            </a>
                        ) : (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                            >
                                {sidebarOpen && <span className="nav-label">{link.label}</span>}
                            </Link>
                        )
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        {sidebarOpen && (
                            <>
                                <p className="user-name">{scholarProfile?.name || user?.fullName || ''}</p>
                                <p className="user-role">{user?.role?.toUpperCase()}</p>
                            </>
                        )}
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        {sidebarOpen && 'Logout'}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`main-content ${sidebarOpen && !isMobile ? '' : 'expanded'}`}>
                <header className="content-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {isMobile && (
                        <button
                            className="hamburger-menu"
                            onClick={() => setSidebarOpen(true)}
                            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                        >
                            ☰
                        </button>
                    )}
                    <h1>My Publications</h1>
                </header>

                <div className="content-body">
                    {/* Statistics Cards */}
                    {(() => {
                        const displayTotal = publications ? publications.length : (stats?.total || 0);
                        const displayJournals = publications ? publications.filter(p => p.category === 'journal').length : (stats?.journals || 0);
                        const displayConferences = publications ? publications.filter(p => p.category === 'conference').length : (stats?.conferences || 0);
                        const displayScopus = publications ? publications.filter(p => p.scopusIndexed).length : (stats?.scopusIndexed || 0);

                        return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                                <div className="stat-card" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)', color: 'white' }}>
                                    <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: 'white' }}>{displayTotal}</div>
                                    <div className="stat-label" style={{ fontSize: '0.875rem', color: 'white', marginBottom: '0.5rem' }}>Total Publications</div>
                                </div>
                                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                                    <div className="stat-value">{displayJournals}</div>
                                    <div className="stat-label" style={{ color: 'black' }}>Journal Papers</div>
                                </div>
                                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                                    <div className="stat-value">{displayConferences}</div>
                                    <div className="stat-label" style={{ color: 'black' }}>Conference Papers</div>
                                </div>
                                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                                    <div className="stat-value">{displayScopus}</div>
                                    <div className="stat-label" style={{ color: 'black' }}>Scopus Indexed</div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Compliance Status */}
                    {/* {compliance && (
                        <div className="card" style={{ marginBottom: '2rem' }}>
                            <div className="card-header">
                                <h3>Publication Compliance Status</h3>
                            </div>
                            <div className="card-body">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div style={{
                                        background: compliance.scopusConferences.met ? '#dcfce7' : '#fee2e2',
                                        padding: '1rem',
                                        borderRadius: '0.5rem',
                                        border: `2px solid ${compliance.scopusConferences.met ? '#16a34a' : '#dc2626'}`
                                    }}>
                                        <h4 style={{ color: compliance.scopusConferences.met ? '#15803d' : '#991b1b', marginBottom: '0.5rem', marginTop: 0 }}>
                                            Scopus Conferences
                                        </h4>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: compliance.scopusConferences.met ? '#15803d' : '#991b1b' }}>
                                            {compliance.scopusConferences.current} / {compliance.scopusConferences.required}
                                        </div>
                                        <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: compliance.scopusConferences.met ? '#15803d' : '#991b1b' }}>
                                            {compliance.scopusConferences.met ? '✓ Requirement Met' : '✗ Not Met'}
                                        </div>
                                    </div>

                                    <div style={{
                                        background: compliance.journals.met ? '#dcfce7' : '#fee2e2',
                                        padding: '1rem',
                                        borderRadius: '0.5rem',
                                        border: `2px solid ${compliance.journals.met ? '#16a34a' : '#dc2626'}`
                                    }}>
                                        <h4 style={{ color: compliance.journals.met ? '#15803d' : '#991b1b', marginBottom: '0.5rem', marginTop: 0 }}>
                                            Q1/Q2/Q3 Journals
                                        </h4>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: compliance.journals.met ? '#15803d' : '#991b1b' }}>
                                            {compliance.journals.current} / {compliance.journals.required}
                                        </div>
                                        <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: compliance.journals.met ? '#15803d' : '#991b1b' }}>
                                            {compliance.journals.met ? '✓ Requirement Met' : '✗ Not Met'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* Download PDF Card */}
                    <div className="card" style={{ marginBottom: '2rem' }}>
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                            <div>
                                <h3 style={{ margin: 0 }}>📄 Download Publications PDF Report</h3>
                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                    Download a combined PDF report containing all your published papers along with summary credentials.
                                </p>
                            </div>
                            <button
                                className="btn btn-primary"
                                onClick={handleDownloadPDF}
                                disabled={isPdfGenerating || publications.length === 0}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#1e3a8a', borderColor: '#1e3a8a', cursor: 'pointer' }}
                            >
                                {isPdfGenerating ? 'Generating PDF...' : `📥 Download Combined PDF (${publications.length})`}
                            </button>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h3>Publications Management</h3>
                        </div>
                        <div className="card-body">
                            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                                <button className="btn btn-primary" onClick={toggleAddForm}>
                                    {showAddForm ? 'Cancel' : (editingId ? 'Edit Publication' : '+ Add Publication')}
                                </button>
                            </div>

                            {/* Add Publication Form */}
                            {showAddForm && (
                                <form onSubmit={handleSubmit} style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem', marginBottom: '2rem' }}>
                                    <h3 style={{ marginBottom: '1rem', marginTop: 0 }}>{editingId ? 'Edit Publication' : 'Add New Publication'}</h3>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div className="form-group">
                                            <label className="form-label">Category *</label>
                                            <select name="category" value={formData.category} onChange={handleChange} className="form-input" required>
                                                <option value="journal">Journal</option>
                                                <option value="conference">Conference</option>
                                                <option value="book-chapter">Book Chapter</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Status *</label>
                                            <select name="status" value={formData.status} onChange={handleChange} className="form-input" required>
                                                <option value="submitted">Submitted</option>
                                                <option value="under review">Under Review</option>
                                                <option value="accepted">Accepted</option>
                                                <option value="published">Published</option>
                                            </select>
                                        </div>

                                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                            <label className="form-label">Title *</label>
                                            <input type="text" name="title" value={formData.title} onChange={handleChange} className="form-input" required />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Authors (comma-separated, max 4) *</label>
                                            <input type="text" name="authors" value={formData.authors} onChange={handleChange} className="form-input" required placeholder="Author1, Author2, Author3" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Corresponding Author *</label>
                                            <input type="text" name="correspondingAuthor" value={formData.correspondingAuthor} onChange={handleChange} className="form-input" required />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Journal/Conference Name *</label>
                                            <input type="text" name="venue" value={formData.venue} onChange={handleChange} className="form-input" required />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Publication Date</label>
                                            <input type="date" name="publicationDate" value={formData.publicationDate} onChange={handleChange} className="form-input" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">DOI/ gdrive link (proof)</label>
                                            <input type="text" name="doi" value={formData.doi} onChange={handleChange} className="form-input" placeholder="10.1234/example" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Year *</label>
                                            <input type="number" name="year" value={formData.year} onChange={handleChange} className="form-input" required min="2020" max="2030" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Volume</label>
                                            <input type="text" name="volume" value={formData.volume} onChange={handleChange} className="form-input" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Issue</label>
                                            <input type="text" name="issue" value={formData.issue} onChange={handleChange} className="form-input" />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Pages</label>
                                            <input type="text" name="pages" value={formData.pages} onChange={handleChange} className="form-input" placeholder="100-110" />
                                        </div>

                                        {formData.category === 'journal' && (
                                            <div className="form-group">
                                                <label className="form-label">Quartile (SCImago Journal Ranking)</label>
                                                <select name="quartile" value={formData.quartile} onChange={handleChange} className="form-input">
                                                    <option value="">Select Quartile</option>
                                                    <option value="NA">NA</option>
                                                    <option value="Q1">Q1</option>
                                                    <option value="Q2">Q2</option>
                                                    <option value="Q3">Q3</option>
                                                    <option value="Q4">Q4</option>
                                                </select>
                                            </div>
                                        )}

                                        <div className="form-group" style={{ gridColumn: '1 / -1', display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.5rem', background: '#f1f5f9', padding: '0.75rem 1rem', borderRadius: '0.375rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#1e293b' }}>
                                                <input
                                                    type="checkbox"
                                                    name="scopusIndexed"
                                                    checked={formData.scopusIndexed}
                                                    onChange={handleChange}
                                                    style={{ width: '1.1rem', height: '1.1rem', accentColor: '#1e3a8a', cursor: 'pointer' }}
                                                />
                                                <span>Scopus Indexed</span>
                                            </label>

                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#1e293b' }}>
                                                <input
                                                    type="checkbox"
                                                    name="trfAcknowledgement"
                                                    checked={formData.trfAcknowledgement}
                                                    onChange={handleChange}
                                                    style={{ width: '1.1rem', height: '1.1rem', accentColor: '#1e3a8a', cursor: 'pointer' }}
                                                />
                                                <span>TRF Acknowledgement</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '1rem' }}>
                                        <button type="submit" className="btn btn-primary" disabled={loading}>
                                            {loading ? (editingId ? 'Updating...' : 'Adding...') : (editingId ? 'Update Publication' : 'Add Publication')}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Publications List */}
                            {publications.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                                    No publications added yet. Click "Add Publication" to get started.
                                </p>
                            ) : (
                                <div className="table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Category</th>
                                                <th>Title</th>
                                                <th>Venue</th>
                                                <th>Date / Year</th>
                                                <th>Status</th>
                                                <th style={{ textAlign: 'center' }}>TRF Ack</th>
                                                <th style={{ textAlign: 'center' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {publications.map((pub) => (
                                                <tr key={pub.id}>
                                                    <td>
                                                        <span style={{
                                                            background: pub.category === 'journal' ? '#dbeafe' : '#dcfce7',
                                                            color: pub.category === 'journal' ? '#1e40af' : '#15803d',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}>
                                                            {pub.category}
                                                        </span>
                                                    </td>
                                                    <td>{pub.title}</td>
                                                    <td>{pub.venue}</td>
                                                    <td><span style={{ fontWeight: 600, color: '#1e3a8a' }}>{formatPubDate(pub)}</span></td>
                                                    <td>
                                                        <span style={{
                                                            background: pub.status === 'published' ? '#dcfce7' : pub.status === 'accepted' ? '#e0f2fe' : pub.status === 'under review' ? '#f3e8ff' : '#fef3c7',
                                                            color: pub.status === 'published' ? '#15803d' : pub.status === 'accepted' ? '#0369a1' : pub.status === 'under review' ? '#6b21a8' : '#92400e',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            textTransform: 'capitalize'
                                                        }}>
                                                            {pub.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {pub.trfAcknowledgement ? '✓' : '✗'}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            onClick={() => handleEdit(pub)}
                                                            className="btn btn-secondary btn-small"
                                                            style={{ marginRight: '0.5rem' }}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(pub.id)}
                                                            className="btn btn-danger btn-small"
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Publications;
